import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FC,
} from "react";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { Button, Input, Modal } from "../../../../shared";
import MonthlySchedule from "../../Schedule/MonthlySchedule";
import {
  isMonthlyScheduleValid,
  type MonthlyAutoStartAnchor,
  type MonthlyScheduleValues,
} from "../../monthlyScheduleData";

import styles from "./EditMonthlyChallengeModal.module.css";

/**
 * Canonical value contract for the Monthly Challenge edit modal — the campaign
 * Name plus the Schedule step's date/time window. Mirrors the Season edit
 * modal's `EditSeasonValues` (composed of the wizard's Overview + Schedule
 * slices) so a single API mapper serves both the modal and the wizard.
 */
export type EditMonthlyChallengeValues = {
  name: string;
  schedule: MonthlyScheduleValues;
};

/**
 * Whether the primary Save button may enable: a non-empty name and a valid
 * window. Delegates the window rule to the wizard's `isMonthlyScheduleValid`
 * so the modal and the wizard's Schedule gate never drift. Exported so the
 * suite can exercise the rule independently of the rendered UI.
 */
export const isEditMonthlyChallengeValid = (
  values: EditMonthlyChallengeValues,
): boolean =>
  values.name.trim().length > 0 && isMonthlyScheduleValid(values.schedule);

export type EditMonthlyChallengeModalProps = {
  open: boolean;
  /** Current challenge name, rendered inside the modal title in quotes. */
  challengeName: string;
  initialValues: EditMonthlyChallengeValues;
  /** Fired with the latest snapshot on Save — only while the Save gate is met. */
  onSave: (values: EditMonthlyChallengeValues) => void;
  /** Fired on cancel / close (top-right, backdrop, escape). */
  onClose: () => void;
  /** Shows the button loader while the parent's update request is in flight. */
  isSaving?: boolean;
  /** Copy for the primary action. A Draft reads "Schedule" (Save promotes it
   *  out of draft); a Scheduled campaign reads "Save" (in-place re-time).
   *  Defaults to "Save". */
  saveLabel?: string;
  /** The active monthly challenge this one auto-starts after — forwarded to the
   *  embedded `MonthlySchedule` so toggling auto-start fills (and locks) the
   *  start one minute after that challenge's end. */
  autoStartAnchor?: MonthlyAutoStartAnchor | null;
};

/**
 * Pencil-icon edit modal for the editable (Draft / Scheduled) Monthly Details
 * General card. Exposes the challenge Name and the Schedule window — matching
 * the fields the Season edit modal exposes — so an admin can rename or re-time
 * a challenge without unscheduling it. Controlled-output: the parent owns
 * persistence via `onSave`; the modal keeps its own draft so an in-progress
 * edit can be abandoned by closing.
 */
const EditMonthlyChallengeModal: FC<EditMonthlyChallengeModalProps> = ({
  open,
  challengeName,
  initialValues,
  onSave,
  onClose,
  isSaving = false,
  saveLabel = AppStrings.GAME_PASS_MONTHLY_DETAILS_EDIT_SAVE,
  autoStartAnchor = null,
}) => {
  const [draft, setDraft] = useState<EditMonthlyChallengeValues>(initialValues);

  // Re-seed each open (and if the parent swaps `initialValues` while open) so a
  // stale draft from a previous session can't leak into the next.
  useEffect(() => {
    if (open) setDraft(initialValues);
  }, [open, initialValues]);

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setDraft((current) => ({ ...current, name: value }));
    },
    [],
  );

  const handleScheduleChange = useCallback((next: MonthlyScheduleValues) => {
    setDraft((current) => ({ ...current, schedule: next }));
  }, []);

  const canSave = useMemo(() => isEditMonthlyChallengeValid(draft), [draft]);

  const handleSave = useCallback(() => {
    if (!isEditMonthlyChallengeValid(draft)) return;
    onSave(draft);
  }, [draft, onSave]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      destroyOnHidden
      title={
        <span
          className={styles.titleRow}
          data-testid="edit-monthly-modal-title"
        >
          <span>{`${AppStrings.GAME_PASS_MONTHLY_DETAILS_EDIT_TITLE_PREFIX} `}</span>
          <span className={styles.titleName}>{`"${challengeName}"`}</span>
        </span>
      }
      footer={
        <div className={styles.footer}>
          <Button
            intent="neutral"
            variant="outline"
            onClick={onClose}
            data-testid="edit-monthly-modal-cancel"
          >
            {AppStrings.CANCEL}
          </Button>
          <Button
            intent="primary"
            onClick={handleSave}
            disabled={!canSave || isSaving}
            loading={isSaving}
              data-testid="edit-monthly-modal-save"
            >
              {saveLabel}
            </Button>
        </div>
      }
    >
      <div className={styles.body} data-testid="edit-monthly-modal">
        {/* ── General (Name) ───────────────────────────────────────────── */}
        <section
          className={styles.section}
          aria-labelledby="edit-monthly-modal-general-heading"
        >
          <h3
            id="edit-monthly-modal-general-heading"
            className={styles.sectionHeader}
          >
            {AppStrings.GAME_PASS_MONTHLY_DETAILS_EDIT_GENERAL_HEADING}
          </h3>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {`${AppStrings.MONTHLY_CHALLENGE_NAME_LABEL}*`}
            </span>
            <Input
              value={draft.name}
              placeholder={AppStrings.MONTHLY_CHALLENGE_NAME_PLACEHOLDER}
              onChange={handleNameChange}
              data-testid="edit-monthly-modal-name"
            />
          </label>
        </section>

        <hr className={styles.divider} aria-hidden="true" />

        {/* ── Scheduling (self-headed by MonthlySchedule) ──────────────── */}
        <MonthlySchedule
          values={draft.schedule}
          onValuesChange={handleScheduleChange}
          autoStartAnchor={autoStartAnchor}
          testIdPrefix="edit-monthly-modal"
        />
      </div>
    </Modal>
  );
};

export default EditMonthlyChallengeModal;
